/**
 * The deterministic rubric: twenty points across seven dimensions.
 *
 * Grading never runs on its own — the UI calls it only when the student presses
 * Grade, because all rule feedback is deliberately withheld until then. The
 * report is also withheld unless the line is complete; a half-written exercise
 * gets its findings but no score.
 */

import type { Exercise, GradeDimension, GradeReport, RulesProfile } from './types';
import { measureCount } from './types';
import { analyze } from './analysis';
import { diatonicDistance } from './pitch';
import { rulesFor } from './rules';

/** Score an exercise. `analysis` is returned so callers need not re-derive it. */
export function grade(ex: Exercise, profile: RulesProfile): GradeReport {
  const A = analyze(ex, profile);
  const R = rulesFor(profile);
  const n = measureCount(ex);
  const complete = ex.cp.every((d) => d != null);
  const clamp = (x: number) => Math.max(0, x);
  const withCode = (code: string) => A.findings.filter((f) => f.code === code).length;
  const inDim = (dim: string) => A.findings.filter((f) => f.dimension === dim).length;

  const dims: GradeDimension[] = [];

  // Vertical consonance: dissonances and crossings cost two points each, the
  // softer spacing and unison faults one.
  const heavy = A.findings.filter(
    (f) => f.dimension === 'cons' && f.code !== 'UNI' && f.code !== 'WIDE',
  ).length;
  const soft = withCode('UNI') + withCode('WIDE');
  dims.push({
    name: 'Vertical consonance',
    max: 5,
    got: clamp(5 - 2 * heavy - soft),
    assists: ['labels', 'allowed'],
  });

  dims.push({
    name: 'Opening, cadence, final',
    max: 3,
    got: clamp(3 - inDim('form')),
    assists: ['allowed'],
  });

  dims.push({
    name: 'Perfect-interval handling',
    max: 4,
    got: clamp(4 - withCode('PAR') * 2 - withCode('DIR') - withCode('PP2')),
    assists: [],
  });

  dims.push({ name: 'Imperfect runs', max: 2, got: clamp(2 - inDim('run')), assists: [] });

  // Gran forgives the first repeated tone; Lim forgives none.
  const repeats = inDim('rep');
  dims.push({
    name: R.repeatedNote === 'sparing' ? 'Repeated notes in moderation' : 'No repeated notes',
    max: 2,
    got: R.repeatedNote === 'sparing' ? clamp(2 - Math.max(0, repeats - 1)) : clamp(2 - repeats),
    assists: [],
  });

  const motions = A.motions.filter((m): m is NonNullable<typeof m> => m != null);
  const contraryFraction = motions.length
    ? motions.filter((m) => m === 'c').length / motions.length
    : 0;
  dims.push({
    name: 'Contrary-motion balance',
    max: 2,
    got: contraryFraction >= 0.5 ? 2 : contraryFraction >= 0.35 ? 1 : 0,
    assists: [],
  });

  // Melodic line: one point for a stepwise majority, one for a clean shape.
  let melodic = 0;
  const intervalsWalked: number[] = [];
  for (let i = 1; i < n; i++) {
    if (ex.cp[i] != null && ex.cp[i - 1] != null) {
      intervalsWalked.push(Math.abs(diatonicDistance(ex.cp[i]!, ex.cp[i - 1]!, ex)));
    }
  }
  if (intervalsWalked.length && intervalsWalked.filter((s) => s === 1).length / intervalsWalked.length >= 0.6) {
    melodic++;
  }
  const melodicWarnings = A.findings.filter((f) => f.dimension === 'mel').length;
  if (A.climaxOk && melodicWarnings === 0) melodic++;
  dims.push({ name: 'Melodic line (steps, climax)', max: 2, got: melodic, assists: [] });

  const total = dims.reduce((s, d) => s + d.got, 0);
  return { dims, total, max: 20, complete, analysis: A };
}
